import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Col, Collapse, Form, Row } from 'react-bootstrap';
import { ChevronDown, ChevronRight, ChevronUp, Search, Telephone } from 'react-bootstrap-icons';
import { ENDPOINTS } from '../../helpers/constants';
import { boardRequest } from './helpers';
import LoadMore from './LoadMore';
import {
  CardSkeletonGrid,
  LoadingStatus,
  PipelineCardSkeleton,
  SectionHeaderSkeleton,
  StatButtonSkeleton
} from './Skeletons';

const pipelineColumns = [
  { key: 'new leads', label: 'New Leads' },
  { key: 'appointment set', label: 'Appointment set' },
  { key: 'left deposit', label: 'Left deposit', also: ['deposit left'] },
  { key: 'clearing conditions', label: 'Clearing conditions' }
];

const mergeGroups = (current, incoming) => {
  const groups = (current || []).map(group => ({ ...group, contacts: [...group.contacts] }));
  (incoming || []).forEach(group => {
    const found = groups.find(item => item.status === group.status);
    if (!found) {
      groups.push(group);
      return;
    }
    const seen = new Set(found.contacts.map(lead => lead._id));
    found.count = group.count;
    found.contacts.push(...group.contacts.filter(lead => !seen.has(lead._id)));
  });
  return groups;
};

function LeadRow({ lead, onOpen }) {
  return (
    <Col md={6} xl={4} className="mb-3">
      <div
        className={`today-row border rounded d-flex align-items-center p-3 h-100 ${lead.exported ? '' : 'bg-white'}`}
        style={lead.exported ? { backgroundColor: '#f0f0f0' } : undefined}
        role="button"
        onClick={() => onOpen(lead)}
      >
        <div className="flex-fill min-width-0">
          <div className="font-weight-bold text-dark text-truncate">{lead.buyer}</div>
          <div className={`small text-truncate ${lead.bucket === 'overdue' ? 'text-danger' : 'text-muted'}`}>
            {lead.reason}
          </div>
        </div>
        {lead.cellPhone ? (
          <Button
            as="a"
            href={`tel:${lead.cellPhone}`}
            variant="light"
            size="sm"
            className="ml-2"
            title={`Call ${lead.buyer}`}
            onClick={event => event.stopPropagation()}
          >
            <Telephone />
          </Button>
        ) : null}
        <ChevronRight className="text-muted ml-2" />
      </div>
    </Col>
  );
}

function StatusSection({ group, onOpen }) {
  return (
    <section className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h5 font-weight-bold mb-0">{group.status}</h3>
        <Badge variant="light" className="border">
          {group.count || group.contacts.length} customers
        </Badge>
      </div>
      <Row>
        {group.contacts.map(lead => (
          <LeadRow key={lead._id} lead={lead} onOpen={onOpen} />
        ))}
      </Row>
    </section>
  );
}

function ImportedContacts({ count, groups, open, onToggle, hasMore, loading, onLoad, onOpen }) {
  if (!count) return null;
  return (
    <div className="mb-4">
      <h3
        className="h6 text-primary d-flex align-items-center mb-3"
        style={{ cursor: 'pointer' }}
        onClick={onToggle}
      >
        {open ? <ChevronUp className="mr-1" /> : <ChevronDown className="mr-1" />}
        {open ? 'Hide' : 'Show'} Imported Contacts
        <span className="ml-1">({count})</span>
      </h3>
      <Collapse in={open}>
        <div>
          {(groups || []).map(group => (
            <StatusSection key={group.status} group={group} onOpen={onOpen} />
          ))}
          {open && <LoadMore hasMore={hasMore} loading={loading} onLoad={onLoad} />}
        </div>
      </Collapse>
    </div>
  );
}

function PipelineTab({ scopeQuery, reloadKey = 0, onOpen }) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importedOpen, setImportedOpen] = useState(false);
  const [loadingImported, setLoadingImported] = useState(false);
  const importedOpenRef = useRef(false);
  const requestId = useRef(0);
  const importedRequestId = useRef(0);
  const showingAll = status === 'all';
  const tiles = pipeline?.tiles?.length ? pipeline.tiles : pipelineColumns.map(column => ({ status: column.label, count: 0 }));

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (page, append) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      const response = await boardRequest(ENDPOINTS.CONTACTS_PIPELINE, scopeQuery, {
        page,
        status,
        search,
        fresh: !append && reloadKey ? '1' : '0'
      });
      if (id !== requestId.current) return;
      setLoading(false);
      setLoadingMore(false);
      if (!response) return;
      setPipeline(current =>
        append && current
          ? {
              ...response,
              groups: mergeGroups(current.groups, response.groups),
              importedGroups: current.importedGroups || [],
              importedPage: current.importedPage,
              importedHasMore: current.importedHasMore
            }
          : { ...response, importedGroups: [] }
      );
      if (!append && importedOpenRef.current) await loadImportedRef.current(1, false);
    },
    [scopeQuery, status, search, reloadKey]
  );
  const loadImportedRef = useRef(async () => {});
  importedOpenRef.current = importedOpen;

  const loadImported = useCallback(
    async (page, append) => {
      const id = ++importedRequestId.current;
      setLoadingImported(true);
      const response = await boardRequest(ENDPOINTS.CONTACTS_PIPELINE, scopeQuery, {
        page: 1,
        importedPage: page,
        status,
        search,
        imported: '1',
        fresh: !append && reloadKey ? '1' : '0'
      });
      if (id !== importedRequestId.current) return;
      setLoadingImported(false);
      if (!response) return;
      setPipeline(current => ({
        ...(current || response),
        importedCount: response.importedCount,
        importedPage: response.importedPage,
        importedHasMore: response.importedHasMore,
        importedGroups: append ? mergeGroups(current?.importedGroups, response.importedGroups) : response.importedGroups
      }));
    },
    [scopeQuery, status, search, reloadKey]
  );
  loadImportedRef.current = loadImported;

  useEffect(() => {
    load(1, false);
  }, [load]);

  useEffect(() => {
    if (importedOpen) loadImportedRef.current(1, false);
  }, [importedOpen]);

  const loadMore = () => {
    if (!pipeline?.hasMore || loading || loadingMore) return;
    load((pipeline.page || 1) + 1, true);
  };

  const loadMoreImported = () => {
    if (!pipeline?.importedHasMore || loadingImported) return;
    loadImported((pipeline.importedPage || 1) + 1, true);
  };

  return (
    <>
      <Row className="align-items-end mb-4">
        <Col md={7} className="mb-3 mb-md-0">
          <div className="text-primary small font-weight-bold mb-1">MY ACTIVE CUSTOMERS</div>
          <h2 className="font-weight-bold mb-1">Pipeline</h2>
          <p className="text-muted mb-0">Each column is a Contact Status. Due and overdue customers come first.</p>
        </Col>
        <Col md={5}>
          <div className="d-flex align-items-center border bg-white rounded px-3">
            <Search className="text-muted mr-2" />
            <Form.Control
              className="border-0 px-0 shadow-none"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder="Search pipeline"
            />
          </div>
        </Col>
      </Row>

      {loading ? (
        <>
          <LoadingStatus label="Loading the book" />
          <Row className="mb-4">
            {Array.from({ length: 5 }, (_, index) => (
              <StatButtonSkeleton key={index} />
            ))}
          </Row>
          <section className="mb-4">
            <SectionHeaderSkeleton />
            <CardSkeletonGrid count={3} Card={PipelineCardSkeleton} />
          </section>
          <section className="mb-4">
            <SectionHeaderSkeleton />
            <CardSkeletonGrid count={3} Card={PipelineCardSkeleton} />
          </section>
        </>
      ) : (
      <Row className="mb-4">
        <Col xs={6} md className="mb-2">
          <button
            type="button"
            className={`btn text-left w-100 px-3 py-2 ${showingAll ? 'btn-primary' : 'btn-light border bg-white'}`}
            onClick={() => setStatus('all')}
          >
            <div className="h5 font-weight-bold mb-0">{pipeline?.listTotal || 0}</div>
            <div className={`small ${showingAll ? '' : 'text-muted'}`}>All</div>
          </button>
        </Col>
        {tiles.map(tile => {
          const selected = !showingAll && tile.status === status;
          return (
            <Col key={tile.status} xs={6} md className="mb-2">
              <button
                type="button"
                className={`btn text-left w-100 px-3 py-2 ${selected ? 'btn-primary' : 'btn-light border bg-white'}`}
                onClick={() => setStatus(tile.status)}
              >
                <div className="h5 font-weight-bold mb-0">{tile.count}</div>
                <div className={`small ${selected ? '' : 'text-muted'}`}>{tile.status}</div>
              </button>
            </Col>
          );
        })}
      </Row>
      )}

      {!loading && (
        <ImportedContacts
          count={pipeline?.importedCount || 0}
          groups={pipeline?.importedGroups}
          open={importedOpen}
          onToggle={() => setImportedOpen(current => !current)}
          hasMore={pipeline?.importedHasMore}
          loading={loadingImported}
          onLoad={loadMoreImported}
          onOpen={onOpen}
        />
      )}
      {!loading &&
        (pipeline?.groups || []).map(group => (
          <StatusSection key={group.status} group={group} onOpen={onOpen} />
        ))}
      {!loading && <LoadMore hasMore={pipeline?.hasMore} loading={loadingMore} onLoad={loadMore} />}

      {!loading && (pipeline?.groups || []).length === 0 && !(pipeline?.importedCount > 0) && (
        <div className="bg-white rounded p-4 text-center text-muted" style={{ border: '1px dashed #dee2e6' }}>
          No customers in this status
        </div>
      )}
    </>
  );
}

export default PipelineTab;
